import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchTransactionDetailsThunk, updateBusinessStatus } from "../../../services/MasterApi";
import { STRIPE_CONFIRM_URL } from "../../../services/const";

export default function ApplicationDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const token = sessionStorage.getItem("bb_token");
  const storedTxId = sessionStorage.getItem("bb_transactionId");
  const activeTxId = id || storedTxId;

  const [subtotal, setSubtotal] = useState(0);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [isSyncingStripe, setIsSyncingStripe] = useState(false);
  const [stripeSyncMsg, setStripeSyncMsg] = useState(null);

  const handleSyncWithStripe = async () => {
    if (!activeTxId) return;
    setIsSyncingStripe(true);
    setStripeSyncMsg(null);
    try {
      const response = await fetch(STRIPE_CONFIRM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transactionId: activeTxId }),
      });
      const resData = await response.json();
      if (response.ok && resData.code === 200) {
        setStripeSyncMsg({ type: "success", text: "Stripe payment verified! Transaction marked as Paid." });
        if (token) {
          dispatch(fetchTransactionDetailsThunk({ token, transactionId: activeTxId }));
        }
      } else {
        setStripeSyncMsg({
          type: "warning",
          text: resData.message || "No succeeded Stripe payment found for this transaction."
        });
      }
    } catch (err) {
      setStripeSyncMsg({ type: "error", text: "Failed to connect to Stripe server." });
    } finally {
      setIsSyncingStripe(false);
      setTimeout(() => setStripeSyncMsg(null), 7000);
    }
  };

  useEffect(() => {
    if (activeTxId && token) {
      sessionStorage.setItem("bb_transactionId", activeTxId);
      dispatch(fetchTransactionDetailsThunk({ token, transactionId: activeTxId }));
    }
  }, [dispatch, token, activeTxId]);

  const transactionData = useSelector((state) => state.transactionDetails);
  const { data: businessData, isLoading } = transactionData;

  function getDateTime(newDate) {
    if (!newDate) return "N/A";
    const date = new Date(newDate);
    const options = { weekday: "short", month: "short", day: "numeric", year: "numeric" };
    return date.toLocaleDateString("en-US", options);
  }

  const rawStatus = (businessData?.businessStatus || businessData?.businessTransactionStatus || businessData?.status || "New").trim();
  const isApproved = rawStatus.toLowerCase() === "approved" || rawStatus.toLowerCase() === "completed";
  const isNew = rawStatus.toLowerCase() === "new";

  const getStatusBadge = () => {
    if (isApproved) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
          Completed / Approved
        </span>
      );
    }
    if (isNew) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
          New Application
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
        <span className="w-2 h-2 rounded-full bg-amber-500"></span>
        In-progress
      </span>
    );
  };

  useEffect(() => {
    let sum = 0;
    if (businessData) {
      if (businessData?.packages?.price) {
        sum += parseFloat(businessData.packages.price);
      }
      if (businessData?.scorp?.price) {
        sum += parseFloat(businessData.scorp.price);
      }
      if (businessData?.expeditedProcess?.price) {
        sum += parseFloat(businessData.expeditedProcess.price);
      }
      if (businessData?.foundersKit?.price) {
        sum += parseFloat(businessData.foundersKit.price);
      }
      if (businessData?.operationAgreement?.price) {
        sum += parseFloat(businessData.operationAgreement.price);
      }
    }
    setSubtotal(sum);
  }, [businessData]);

  const filingFee =
    businessData?.businessPayment?.filingFee != null
      ? parseFloat(businessData.businessPayment.filingFee)
      : 100.0;

  const total =
    businessData?.businessPayment?.totalPrice != null
      ? parseFloat(businessData.businessPayment.totalPrice)
      : subtotal + filingFee;

  const handleStatusChange = async (e) => {
    const newStatusId = e.target.value;
    if (!newStatusId || !activeTxId) return;

    try {
      setIsUpdatingStatus(true);
      setStatusMessage(null);
      await updateBusinessStatus(activeTxId, newStatusId);
      setStatusMessage({ type: "success", text: "Status updated successfully." });
      // Refresh current transaction details
      dispatch(fetchTransactionDetailsThunk({ token, transactionId: activeTxId }));
    } catch (err) {
      setStatusMessage({ type: "error", text: err.message || "Failed to update status." });
    } finally {
      setIsUpdatingStatus(false);
      setTimeout(() => setStatusMessage(null), 4000);
    }
  };

  const currentStatusId = isNew ? "1" : isApproved ? "3" : "2";

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#f8fafc] py-5 px-4 sm:px-8 font-sans">
      <div className="w-full space-y-4">
        {/* Top Action Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/table"
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-slate-600 hover:text-[#1a73e8] text-[13px] font-medium transition-all shadow-2xs"
          >
            <i className="fa-solid fa-arrow-left text-xs"></i>
            <span>Back to Registered Businesses</span>
          </Link>

          {/* Status Feedback Toast */}
          {statusMessage && (
            <div
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2 transition-all ${
                statusMessage.type === "success"
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              <i
                className={`fa-solid ${
                  statusMessage.type === "success" ? "fa-circle-check" : "fa-circle-exclamation"
                }`}
              ></i>
              <span>{statusMessage.text}</span>
            </div>
          )}
        </div>

        {/* Hero Header Card */}
        <div className="bg-white rounded-xl border border-slate-200/90 p-4 sm:p-5 shadow-2xs">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className="bg-slate-100 text-slate-700 font-semibold text-[11px] px-2.5 py-0.5 rounded-md tracking-wider">
                  #REGD{businessData?.businessTransactionId || activeTxId}
                </span>
                <span className="bg-blue-50 text-[#1a73e8] border border-blue-100 font-semibold text-[11px] px-2.5 py-0.5 rounded-md">
                  {businessData?.businessType?.name || businessData?.businessType?.code || "Corporation"}
                </span>
                {getStatusBadge()}
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                {businessData?.businessName || "Application Details"}
              </h1>
            </div>

            {/* Admin Status Changer & Filing Date */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Status Update Control */}
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-2 rounded-lg">
                <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">
                  Update Status:
                </span>
                <select
                  value={currentStatusId}
                  onChange={handleStatusChange}
                  disabled={isUpdatingStatus}
                  className="bg-white border border-slate-300 rounded px-2.5 py-1 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1a73e8]/20 focus:border-[#1a73e8] cursor-pointer disabled:opacity-60"
                >
                  <option value="1">New Application</option>
                  <option value="2">In-Progress</option>
                  <option value="3">Approved / Completed</option>
                </select>
                {isUpdatingStatus && (
                  <i className="fa-solid fa-spinner animate-spin text-xs text-[#1a73e8]"></i>
                )}
              </div>

              {/* Filing Date Badge */}
              <div className="flex items-center gap-3 bg-slate-50 px-3.5 py-2 rounded-lg border border-slate-200/70">
                <div className="w-7 h-7 rounded-md bg-blue-100/60 flex items-center justify-center text-[#1a73e8]">
                  <i className="fa-regular fa-calendar-check text-xs"></i>
                </div>
                <div>
                  <span className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                    Filing Date
                  </span>
                  <span className="text-xs font-semibold text-slate-700">
                    {getDateTime(businessData?.createDate)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 2-Column Responsive Information Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Card 1: Business Profile & Location */}
          <div className="bg-white rounded-xl border border-slate-200/90 p-4 sm:p-5 shadow-2xs">
            <div className="flex items-center gap-2.5 pb-3 mb-3 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-blue-50 text-[#1a73e8] flex items-center justify-center">
                <i className="fa-solid fa-building text-sm"></i>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900 leading-tight">
                  Business Profile & Location
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">Statutory registered company information</p>
              </div>
            </div>

            <div className="space-y-2 text-[13px]">
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500 font-medium">Legal Business Name:</span>
                <span className="font-semibold text-slate-800 text-right">
                  {businessData?.businessName || "-"}
                </span>
              </div>

              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500 font-medium">Entity Classification:</span>
                <span className="font-semibold text-slate-800 text-right">
                  {businessData?.businessType?.name || businessData?.businessType?.code || "Corporation"}
                </span>
              </div>

              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500 font-medium">Industry Category:</span>
                <span className="font-semibold text-slate-800 text-right">
                  {businessData?.businessCategory?.name || businessData?.businessCategory?.code || "General Enterprise"}
                </span>
              </div>

              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500 font-medium">Street Address:</span>
                <span className="font-semibold text-slate-800 text-right">
                  {businessData?.businessLocation?.street || "-"}
                </span>
              </div>

              <div className="flex justify-between py-1">
                <span className="text-slate-500 font-medium">City, State & Zip:</span>
                <span className="font-semibold text-slate-800 text-right">
                  {businessData?.businessLocation?.city
                    ? `${businessData.businessLocation.city}, ${businessData.businessLocation.state} ${businessData.businessLocation.zipcode}`
                    : "-"}
                </span>
              </div>
            </div>
          </div>

          {/* Card 2: Primary Contact & Members */}
          <div className="bg-white rounded-xl border border-slate-200/90 p-4 sm:p-5 shadow-2xs">
            <div className="flex items-center gap-2.5 pb-3 mb-3 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <i className="fa-solid fa-users text-sm"></i>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900 leading-tight">
                  Primary Contact & Stakeholders
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">Authorised signers and ownership structure</p>
              </div>
            </div>

            {/* Primary Contact Row */}
            <div className="bg-slate-50/80 border border-slate-200/80 rounded-lg p-3 mb-3">
              <span className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                Primary Applicant Contact
              </span>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                <span className="font-semibold text-slate-800 text-[13.5px]">
                  {businessData?.primaryContact?.fullName || "-"}
                </span>
                <span className="text-[11.5px] font-medium text-slate-600 bg-white px-2 py-0.5 rounded border border-slate-200/60 self-start sm:self-auto">
                  {businessData?.primaryContact?.email || "-"}
                </span>
              </div>
            </div>

            {/* Members List */}
            <span className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
              Registered Members / Officers ({businessData?.members?.length || 0})
            </span>

            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {businessData?.members && businessData.members.length > 0 ? (
                businessData.members.map((ele, idx) => (
                  <div
                    key={idx}
                    className="bg-white border border-slate-200/80 rounded-lg p-2.5 flex items-center justify-between shadow-2xs"
                  >
                    <div>
                      <span className="font-semibold text-slate-800 text-[13px] block leading-tight">
                        {ele?.firstName} {ele?.lastName}
                      </span>
                      <span className="text-[11px] text-slate-500">
                        {ele?.memberEmail ? ele.memberEmail : "Designated Officer"}
                      </span>
                    </div>
                    <div className="text-right">
                      {ele?.ownershipPercentage ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {ele.ownershipPercentage}% Ownership
                        </span>
                      ) : (
                        <span className="text-[11px] font-medium text-slate-500 bg-slate-50 px-2 py-0.5 rounded border border-slate-200/60">
                          Officer
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-400 italic py-1">No additional members on record.</p>
              )}
            </div>
          </div>

          {/* Card 3: Selected Packages & Services */}
          <div className="bg-white rounded-xl border border-slate-200/90 p-4 sm:p-5 shadow-2xs">
            <div className="flex items-center gap-2.5 pb-3 mb-3 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <i className="fa-solid fa-box-open text-sm"></i>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900 leading-tight">
                  Selected Package & Services
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">Itemized service tier and add-ons</p>
              </div>
            </div>

            <div className="space-y-2">
              {/* Base Package */}
              <div className="bg-slate-50/80 border border-slate-200/70 rounded-lg p-2.5 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">
                    Formation Tier
                  </span>
                  <span className="text-[13px] font-semibold text-slate-800">
                    {businessData?.packages?.name || "Standard Formation"}
                  </span>
                </div>
                <span className="text-[13.5px] font-bold text-[#1a73e8]">
                  ${businessData?.packages?.price ? parseFloat(businessData.packages.price).toFixed(2) : "0.00"}
                </span>
              </div>

              {/* Agreement */}
              {businessData?.operationAgreement && (
                <div className="bg-slate-50/80 border border-slate-200/70 rounded-lg p-2.5 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">
                      Operating Agreement
                    </span>
                    <span className="text-[13px] font-semibold text-slate-800">
                      {businessData.operationAgreement.name}
                    </span>
                  </div>
                  <span className="text-[13.5px] font-bold text-[#1a73e8]">
                    ${businessData.operationAgreement.price ? parseFloat(businessData.operationAgreement.price).toFixed(2) : "0.00"}
                  </span>
                </div>
              )}

              {/* Processing Speed */}
              {businessData?.expeditedProcess && (
                <div className="bg-slate-50/80 border border-slate-200/70 rounded-lg p-2.5 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">
                      Processing Option
                    </span>
                    <span className="text-[13px] font-semibold text-slate-800">
                      {businessData.expeditedProcess.name}
                    </span>
                  </div>
                  <span className="text-[13.5px] font-bold text-[#1a73e8]">
                    ${businessData.expeditedProcess.price ? parseFloat(businessData.expeditedProcess.price).toFixed(2) : "0.00"}
                  </span>
                </div>
              )}

              {/* Founders Kit */}
              {businessData?.foundersKit && (
                <div className="bg-slate-50/80 border border-slate-200/70 rounded-lg p-2.5 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">
                      Founders Kit & Compliance
                    </span>
                    <span className="text-[13px] font-semibold text-slate-800">
                      {businessData.foundersKit.name}
                    </span>
                  </div>
                  <span className="text-[13.5px] font-bold text-[#1a73e8]">
                    ${businessData.foundersKit.price ? parseFloat(businessData.foundersKit.price).toFixed(2) : "0.00"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Card 4: Financial Summary & Live Payment Status */}
          <div className="bg-white rounded-xl border border-slate-200/90 p-4 sm:p-5 shadow-2xs">
            <div className="flex items-center gap-2.5 pb-3 mb-3 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                <i className="fa-solid fa-receipt text-sm"></i>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900 leading-tight">
                  Payment & Transaction Details
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">Live gateway verification and balance summary</p>
              </div>
            </div>

            <div className="space-y-2 mb-3 text-[13px]">
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500 font-medium">Services Subtotal:</span>
                <span className="font-semibold text-slate-800">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500 font-medium">State Filing Fee:</span>
                <span className="font-semibold text-slate-800">${filingFee.toFixed(2)}</span>
              </div>
            </div>

            {/* Total Banner */}
            <div className="bg-gradient-to-r from-blue-50/90 to-indigo-50/90 border border-blue-200/80 rounded-lg p-3 flex items-center justify-between mb-3">
              <div>
                <span className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  Total Amount
                </span>
                <span className="text-xl font-bold text-[#1a73e8]">
                  ${total.toFixed(2)}
                </span>
              </div>
              <div className="text-right">
                <span className="text-[11px] font-medium text-slate-500">
                  {businessData?.businessPayment?.paymentMode || "Stripe Payment"}
                </span>
              </div>
            </div>

            {/* Live Payment Status Block */}
            {businessData?.businessPayment?.paymentTransactionId ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-check text-xs"></i>
                  </div>
                  <span className="text-xs font-bold text-emerald-800">
                    Payment Verified & Completed
                  </span>
                </div>
                <div className="text-[11.5px] text-emerald-700 font-mono break-all pl-7">
                  Stripe Ref: <strong>{businessData.businessPayment.paymentTransactionId}</strong>
                </div>
                {businessData.businessPayment.payer?.email && (
                  <div className="text-[11px] text-emerald-600 pl-7">
                    Payer: {businessData.businessPayment.payer.email}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-clock text-amber-600 text-sm"></i>
                    <div>
                      <span className="text-xs font-bold text-amber-800 block">
                        Payment Pending
                      </span>
                      <span className="text-[11px] text-amber-600">
                        No gateway payment has been recorded yet.
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={isSyncingStripe}
                    onClick={handleSyncWithStripe}
                    className="px-3 py-1.5 bg-white hover:bg-amber-100 border border-amber-300 text-amber-900 rounded text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer shrink-0 disabled:opacity-50 shadow-sm"
                  >
                    {isSyncingStripe ? (
                      <>
                        <i className="fa-solid fa-circle-notch fa-spin text-xs"></i> Checking Stripe...
                      </>
                    ) : (
                      <>
                        <i className="fa-brands fa-stripe text-base text-[#635bff]"></i> Verify & Sync Stripe
                      </>
                    )}
                  </button>
                </div>

                {stripeSyncMsg && (
                  <div className={`p-2.5 rounded text-xs border ${
                    stripeSyncMsg.type === 'success' 
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
                      : stripeSyncMsg.type === 'warning'
                      ? 'bg-amber-50 text-amber-800 border-amber-200'
                      : 'bg-rose-50 text-rose-800 border-rose-200'
                  }`}>
                    {stripeSyncMsg.text}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
